import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AffiliatePriceInfo {
  price_gbp: number | null;
  previous_price_gbp: number | null;
  availability: string | null;
  price_last_checked: string | null;
  price_drop_at: string | null;
  sale_end_date: string | null;
  affiliate_link: string;
}

const SUPPORTED_HOST_PATTERNS = [
  /amazon\./i,
  /hyundaipowerequipment/i,
  /awin1\.com/i,
  /toolstation/i,
  /screwfix/i,
  /argos\./i,
  /mowdirect/i,
  /machinemart/i,
  /stihl/i,
  /honda/i,
];

function isAffiliateLikeHref(href: string): boolean {
  try {
    const u = new URL(href, window.location.origin);
    if (u.origin === window.location.origin) return false;
    return SUPPORTED_HOST_PATTERNS.some((p) => p.test(u.hostname));
  } catch {
    return false;
  }
}

function extractAffiliateUrls(html: string): string[] {
  const out = new Set<string>();
  const re = /href=["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    if (isAffiliateLikeHref(m[1])) out.add(m[1]);
  }
  return Array.from(out);
}

function chipHtml(info: AffiliatePriceInfo): string {
  if (!info.price_gbp) return "";
  const drop = info.previous_price_gbp && info.previous_price_gbp > info.price_gbp;
  const percent = drop ? Math.round(((info.previous_price_gbp! - info.price_gbp!) / info.previous_price_gbp!) * 100) : null;
  const oos = info.availability && info.availability !== "in_stock";
  const price = `£${info.price_gbp.toFixed(2)}`;
  const wasMarkup = drop ? ` <span style="text-decoration:line-through;opacity:0.7;margin-left:4px">£${info.previous_price_gbp!.toFixed(2)}</span>` : "";
  const pctMarkup = percent ? ` <span style="background:#dc2626;color:#fff;padding:1px 6px;border-radius:9999px;font-size:0.75em;margin-left:4px;font-weight:700">-${percent}%</span>` : "";
  const oosMarkup = oos ? ` <span style="background:#fef3c7;color:#92400e;padding:1px 6px;border-radius:9999px;font-size:0.75em;margin-left:6px">Limited stock</span>` : "";
  return ` <span class="affiliate-price-chip" style="display:inline-flex;align-items:center;gap:4px;background:#16a34a;color:#fff;padding:2px 8px;border-radius:9999px;font-size:0.85em;font-weight:600;margin-left:6px;vertical-align:middle">${price}${wasMarkup}</span>${pctMarkup}${oosMarkup}`;
}

function injectChips(root: HTMLElement, prices: Record<string, AffiliatePriceInfo>) {
  const links = root.querySelectorAll<HTMLAnchorElement>("a[href]");
  links.forEach((a) => {
    if (a.dataset.affiliateChipApplied === "1") return;
    // Skip anchors inside product cards or other not-prose blocks — they render their own pricing.
    if (a.closest(".not-prose, [data-no-price-chip]")) return;
    const info = prices[a.href];
    if (!info) return;
    a.dataset.affiliateChipApplied = "1";
    const tmp = document.createElement("span");
    tmp.innerHTML = chipHtml(info);
    while (tmp.firstChild) a.parentNode?.insertBefore(tmp.firstChild, a.nextSibling);
  });
}

let cache: Record<string, AffiliatePriceInfo> | null = null;
let cacheAt = 0;
const TTL = 10 * 60 * 1000;

export async function fetchAffiliatePrices(urls: string[]): Promise<Record<string, AffiliatePriceInfo>> {
  if (urls.length === 0) return {};
  const now = Date.now();
  if (cache && now - cacheAt < TTL) {
    const hit: Record<string, AffiliatePriceInfo> = {};
    let allHit = true;
    for (const u of urls) {
      if (cache[u]) hit[u] = cache[u];
      else allHit = false;
    }
    if (allHit) return hit;
  }
  const { data, error } = await supabase.functions.invoke("affiliate-price-lookup", { body: { urls } });
  if (error || !data?.prices) return {};
  cache = { ...(cache || {}), ...data.prices };
  cacheAt = now;
  return data.prices;
}

export function useAffiliatePriceAugment(rootRef: React.RefObject<HTMLElement | null>, html: string | null | undefined) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDone(false);
    if (!html || !rootRef.current) return;
    const urls = extractAffiliateUrls(html);
    if (urls.length === 0) { setDone(true); return; }
    let cancelled = false;
    fetchAffiliatePrices(urls).then((prices) => {
      if (cancelled || !rootRef.current) return;
      injectChips(rootRef.current, prices);
      setDone(true);
    });
    return () => { cancelled = true; };
  }, [html, rootRef]);
  return done;
}
